import { CartState } from './../../cart/reducers/cart.reducer';
import { inject, Injectable, Injector, signal, Signal } from '@angular/core';
import { CartEntryEvent, EcommerceTrackingService } from '@boels/helpers';
import { ProductFacade } from '../../product';
import {
  BoelsProductReference,
  BoelsProductReferenceType,
  BOProduct,
  EntryGroup,
  EntryGroupUpdate,
  EntryGroupUpdateAccessory,
  OrderEntry,
  OrderEntryGroup,
} from '@boels/models';
import {
  asyncScheduler,
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  observeOn,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { CartFacade } from '../../cart/facade/cart.facade';
import { filter } from 'rxjs/operators';

export interface AddToCartModalData {
  product?: BOProduct;
  entry?: OrderEntryGroup;
  productToEdit?: BOProduct;
}

export enum AddToCartAction {
  ADD,
  UPDATE,
}

export interface AddToCartModalState {
  mainEntry: Signal<OrderEntry>;
  accessoriesEntries: Signal<Array<OrderEntry>>;
  action: AddToCartAction;
  labels: Signal<{
    header: string;
    submitBtn: string;
  }>;
  existingAccessoriesEntries?: EntryGroupUpdateAccessory[];
  existingMainEntry?: OrderEntry;
}

@Injectable()
export class CartService {
  /** Inject services */
  #cartFacade = inject(CartFacade);
  #productFacade = inject(ProductFacade);
  #ecommerceTrackingService = inject(EcommerceTrackingService);
  #injector = inject(Injector);

  addToCartModalState(
    data: AddToCartModalData,
    onFinished: () => void,
    onError: (error: any) => void,
  ): AddToCartModalState {
    if (!data.product && !data.entry) {
      onError(
        new Error('No product or entry provided for addToCartModalState'),
      );
      return {
        mainEntry: signal({} as OrderEntry),
        accessoriesEntries: signal([]),
        action: AddToCartAction.ADD,
        labels: signal({} as any),
        existingAccessoriesEntries: [],
      };
    }

    const orderEntryMain = {
      ...(data.entry ?? { product: data.product, quantity: 1 }),
      minQuantity: 1,
    };

    const accessoriesEntries = this.getAccessoriesEntries(
      orderEntryMain,
      onFinished,
      onError,
    );

    return {
      mainEntry: signal(orderEntryMain),
      accessoriesEntries: accessoriesEntries,
      action: data.entry ? AddToCartAction.UPDATE : AddToCartAction.ADD,
      labels: signal(this.#addToCartLabels(!!data.entry)),
      existingAccessoriesEntries: orderEntryMain.accessories?.map(
        (entry) =>
          <EntryGroupUpdateAccessory>{
            product: entry.product,
            entryNumber: entry.entryNumber,
            quantity: entry.quantity,
            isAccessory: true,
          },
      ),
      existingMainEntry: data.entry && JSON.parse(JSON.stringify(data.entry)),
    } as AddToCartModalState;
  }

  addToCart(
    addToCartState: AddToCartModalState | undefined,
  ): Observable<CartState> {
    if (!addToCartState)
      return throwError(() => {
        return { message: 'addToCartState is undefined' };
      });

    if (addToCartState.action === AddToCartAction.ADD)
      return this.#postProductsToCart(addToCartState);

    return this.#updateProductsToCart(addToCartState);
  }

  getAccessories(orderEntry: OrderEntryGroup): Observable<OrderEntry[]> {
    return this.#getProductReferences(orderEntry.product as BOProduct).pipe(
      map((references) =>
        references
          .flatMap((ref) => {
            const accessoryEntry = this.#setAccessoryEntry(
              ref,
              orderEntry,
              orderEntry?.accessories?.find(
                (entry) => entry.product?.code === ref.target?.code,
              ),
            );
            return accessoryEntry;
          })
          .sort(
            (a, b) => +(b.isMandatory as boolean) - +(a.isMandatory as boolean),
          ),
      ),
    );
  }

  /**
   * Retrieves the accessories entries for a given order entry.
   * If the product references are already available, it creates the accessory entries directly.
   * Otherwise, it fetches the accessories from the API and processes them.
   *
   * @param orderEntryMain - The main order entry group containing the product and its details.
   * @param onFinished - Callback function to be called when the operation is finished.
   * @param onError - Callback function to be called in case of an error.
   * @returns A Signal containing an array of OrderEntry objects representing the accessories.
   */
  private getAccessoriesEntries(
    orderEntryMain: OrderEntryGroup,
    onFinished: () => void,
    onError: (error: any) => void,
  ): Signal<OrderEntry[]> {
    if (orderEntryMain.product?.productReferences !== undefined) {
      const accessories = orderEntryMain.product.productReferences
        .flatMap((ref: BoelsProductReference) => {
          const accessoryEntry = this.#setAccessoryEntry(
            ref,
            orderEntryMain,
            orderEntryMain?.accessories?.find(
              (entry) => entry.product?.code === ref.target?.code,
            ),
          );
          return accessoryEntry;
        })
        .sort(
          (a, b) => +(b.isMandatory as boolean) - +(a.isMandatory as boolean),
        );
      onFinished();
      return signal(accessories);
    } else {
      return toSignal(
        this.getAccessories(orderEntryMain).pipe(
          catchError((error) => {
            onError(error);
            return EMPTY;
          }),
          take(1),
          finalize(() => onFinished()),
        ),
        {
          initialValue: [],
          injector: this.#injector,
        },
      );
    }
  }

  /**
   * Get the product for the AddToCartModal
   * If the product is missing properties, get the product from the API
   * Else return the AddToCartModalState with the product provided
   * @param data
   * @param onFinished
   * @param handleError
   */
  getProductForAddToCartModal(
    data: AddToCartModalData,
    onFinished: () => void,
    handleError: (error?: any) => void,
  ): Observable<AddToCartModalState> {
    const modalProduct = data.product || data.entry?.product;
    if (!modalProduct)
      return throwError(() => {
        return { message: 'product is undefined' };
      });
    const isProductMissingProperties = !Object.keys(
      modalProduct as BOProduct,
    ).some((key) => ['tax', 'productReferences'].includes(key));

    if (isProductMissingProperties) {
      return this.#productFacade.get(modalProduct.code as string).pipe(
        filter(Boolean),
        take(1),
        tap((product) => {
          if (data.entry) data.entry.product = product as BOProduct;
          else data.product = product as BOProduct;
        }),
        switchMap(() =>
          of(this.addToCartModalState(data, onFinished, handleError)),
        ),
      );
    } else {
      return of(this.addToCartModalState(data, onFinished, handleError));
    }
  }

  recalculateMandatoryAccessoryQuantities(
    addToCartState: AddToCartModalState,
  ): void {
    const mainEntryQuantity = addToCartState.mainEntry().quantity as number;
    const calcMandatoryAccessoryQuantity = (
      mainQuantity: number,
      accessoryEntry: OrderEntry,
    ) => {
      accessoryEntry.quantity = Math.floor(
        (accessoryEntry.requiredQuantity ?? 1) * mainQuantity,
      );
      accessoryEntry.minQuantity = accessoryEntry.quantity;
    };
    addToCartState
      .accessoriesEntries()
      .filter((entry) => entry.isMandatory)
      .forEach((entry) =>
        calcMandatoryAccessoryQuantity(mainEntryQuantity, entry),
      );
  }

  removeOrderEntry(addToCarte: AddToCartModalState, entryNumber: number): void {
    this.#setTrackingAndMeasurement(addToCarte, 'remove');
    this.#cartFacade.removeEntry(entryNumber);
  }

  //#region private methods

  // TODO: Update tracking service for analytics
  #postProductsToCart(
    addToCartState: AddToCartModalState,
  ): Observable<CartState> {
    const entryGroupDTO: EntryGroup = {
      product: {
        code: addToCartState.mainEntry().product?.code as string,
      },
      quantity: addToCartState.mainEntry().quantity as number,
      accessories: this.#getPostEntryGroupDTOAccessories(
        addToCartState.accessoriesEntries(),
      ),
    };
    this.#setTrackingAndMeasurement(addToCartState, 'add');
    return this.#cartFacade.addEntry(entryGroupDTO).pipe(
      tap((cartState: CartState) => {
        if (cartState.error) throw new Error(cartState.errorMessage);
      }),
    );
  }

  #updateProductsToCart(
    addToCartState: AddToCartModalState,
  ): Observable<CartState> {
    const entryUpdateDTO: EntryGroupUpdate = {
      entryNumber: addToCartState.mainEntry().entryNumber as number,
      quantity: addToCartState.mainEntry().quantity as number,
      accessories: this.#getUpdateEntryGroupDTOAccessories(
        addToCartState.accessoriesEntries(),
        addToCartState.existingAccessoriesEntries,
      ),
    };

    this.#setTrackingAndMeasurement(addToCartState, 'update');
    return this.#cartFacade.updateEntry(entryUpdateDTO).pipe(
      tap((cartState: CartState) => {
        if (cartState.error) throw new Error(cartState.errorMessage);
      }),
    );
  }

  #addToCartLabels(isEditing: boolean): { header: string; submitBtn: string } {
    const headerLabel = `addToCart.addToCartModal.${
      isEditing ? 'editTitle' : 'addTitle'
    }`;
    const addToCartBtnLabel = `addToCart.addToCartModal.${
      isEditing ? 'editBtnLabel' : 'addBtnLabel'
    }`;

    return {
      header: headerLabel,
      submitBtn: addToCartBtnLabel,
    };
  }

  #getProductReferences(
    product: BOProduct,
    type?: BoelsProductReferenceType,
  ): Observable<BoelsProductReference[]> {
    return this.#productFacade.getProductReferences(
      product.code as string,
      type,
    );
  }

  #setAccessoryEntry(
    productReference: BoelsProductReference,
    mainEntry: OrderEntry,
    accessoryEntry?: OrderEntry,
  ): OrderEntry {
    const { target, quantity, referenceType } = productReference;
    const isMandatory = referenceType?.includes('MANDATORY');
    const minQuantity = isMandatory
      ? quantity
        ? quantity * (mainEntry.quantity as number) || 1
        : 1
      : 0;
    const entryQuantity = accessoryEntry?.quantity ?? minQuantity;

    (target as BOProduct).isAccessory = true;
    return {
      entryNumber: accessoryEntry?.entryNumber,
      product: target,
      quantity: entryQuantity,
      isMandatory,
      isAccessory: true,
      minQuantity: minQuantity,
      requiredQuantity: isMandatory ? quantity : undefined,
    };
  }

  #getPostEntryGroupDTOAccessories(accessoriesEntries: OrderEntry[]) {
    return accessoriesEntries
      .filter((entry) => entry.quantity !== 0)
      .map((entry) => ({
        product: { code: entry.product?.code as string },
        quantity: entry.quantity as number,
      }));
  }

  #getUpdateEntryGroupDTOAccessories(
    accessoriesEntries: OrderEntry[],
    existingAccessoriesQuantities?: EntryGroupUpdateAccessory[],
  ) {
    return accessoriesEntries
      .filter(
        (entry) =>
          entry.quantity !== 0 ||
          existingAccessoriesQuantities?.find(
            (e) => e.entryNumber === (entry.entryNumber as number),
          ),
      )
      .filter(
        (entry) =>
          !existingAccessoriesQuantities?.find(
            (e) => e.product?.code === entry.product?.code,
          ) ||
          existingAccessoriesQuantities?.find(
            (e) => e.entryNumber === entry.entryNumber,
          )?.quantity !== entry.quantity,
      )
      .map((entry) => ({
        entryNumber: entry.entryNumber as number,
        quantity: entry.quantity as number,
        product: entry.entryNumber
          ? undefined
          : {
              code: entry.product?.code as string,
            },
      }));
  }

  #setTrackingAndMeasurement(
    addToCartState: AddToCartModalState,
    action: 'add' | 'update' | 'remove',
  ): void {
    this.#cartFacade
      .getActiveCartId()
      .pipe(
        tap((cartId) => {
          const orderEntries = [
            ...[addToCartState.mainEntry()],
            ...addToCartState
              .accessoriesEntries()
              .filter((entry) => entry.quantity && entry.quantity > 0),
          ];
          if (action === 'update') {
            const obsoleteEntries = [
              ...[addToCartState.existingMainEntry],
              ...(addToCartState.existingAccessoriesEntries || []),
            ] as OrderEntry[];
            this.#ecommerceTrackingService.setCartEntryEvent(
              cartId,
              orderEntries,
              CartEntryEvent.UpdateCart,
              obsoleteEntries,
            );
            return;
          }
          this.#ecommerceTrackingService.setCartEntryEvent(
            cartId,
            orderEntries,
            action === 'add'
              ? CartEntryEvent.AddToCart
              : CartEntryEvent.RemoveFromCart,
          );
        }),
        take(1),
        observeOn(asyncScheduler),
      )
      .subscribe();
  }

  //#endregion
}
